import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Checkbox,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Textarea,
} from "@monbits/ui";
import type * as React from "react";
import { useForm } from "react-hook-form";
import LinkInput, { validateUrl } from "src/components/link-input/link-input";
import { CURRENCY, type PaymentParams } from "src/types";
import * as z from "zod";

interface Props {
	onFinish: (values: PaymentParams, reset: () => void) => Promise<void>;
	isFormSubmitting: boolean;
}

const linkPrefix = "https://";

const formSchema = z.object({
	price: z.number().min(0, "Price must be at least 0").max(10000000),
	currency: z.enum(["USD", "EUR"]),
	orderId: z.string().min(1, "Order ID is required").max(255),
	description: z.string().optional(),
	redirectUrl: z
		.string()
		.refine((val) => !val || validateUrl(val, false), {
			message: "Incorrect url value",
		})
		.optional(),
	isTest: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

const PaymentForm: React.FC<Props> = (props: Props) => {
	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			price: 0,
			currency: "USD",
			orderId: "",
			description: "",
			redirectUrl: "",
			isTest: false,
		},
	});

	const onSubmit = async (values: FormValues) => {
		const submissionValues: PaymentParams = {
			...values,
			redirectUrl: values.redirectUrl
				? linkPrefix + values.redirectUrl
				: undefined,
		};
		await props.onFinish(submissionValues, form.reset);
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
				<div className="grid grid-cols-[1fr_auto] gap-4">
					<FormField
						control={form.control}
						name="price"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Price</FormLabel>
								<FormControl>
									<Input
										type="number"
										step="0.01"
										min="0"
										max="10000000"
										{...field}
										onChange={(e) => {
											const value = e.target.value;
											field.onChange(value ? Number.parseFloat(value) : 0);
										}}
										value={field.value || ""}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="currency"
						render={({ field }) => (
							<FormItem className="w-32">
								<FormLabel>Currency</FormLabel>
								<Select
									onValueChange={field.onChange}
									defaultValue={field.value}
								>
									<FormControl>
										<SelectTrigger className="w-full">
											<SelectValue />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{CURRENCY.map((currency) => (
											<SelectItem key={currency} value={currency}>
												{currency}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<FormField
					control={form.control}
					name="orderId"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Order ID</FormLabel>
							<FormControl>
								<Input type="text" placeholder="order-123" {...field} />
							</FormControl>
							<FormDescription>
								Unique order identifier from your system (required)
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="description"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Description</FormLabel>
							<FormControl>
								<Textarea placeholder="Your description" rows={3} {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="redirectUrl"
					render={({ field, fieldState }) => (
						<LinkInput
							label="Customer redirect URL"
							name="redirectUrl"
							placeholder="your-store.com/success"
							required={false}
							value={field.value}
							onChange={field.onChange}
							error={fieldState.error?.message}
						/>
					)}
				/>

				<FormField
					control={form.control}
					name="isTest"
					render={({ field }) => (
						<FormItem className="flex flex-row items-start space-x-3 space-y-0">
							<FormControl>
								<Checkbox
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
							</FormControl>
							<div className="space-y-1 leading-none">
								<FormLabel>Test payment</FormLabel>
								<FormDescription>
									Test payments are processed using testnets like Ethereum
									Goerli
								</FormDescription>
							</div>
						</FormItem>
					)}
				/>

				{/* Hidden submit button for form validation */}
				<Button type="submit" className="sr-only">
					Submit
				</Button>
			</form>
		</Form>
	);
};

export default PaymentForm;
